/* eslint-disable react/prop-types */
// Teams section of the Twilio settings modal: list teams with their members,
// create a team, rename a team (inline edit), delete a team (armed-button
// confirm — no window.confirm), and add/remove members. Members are CMS users
// with access; a user can belong to multiple teams. Every mutation is
// super-user only (server-enforced) and reloads the shared team list via
// onReload so the Routing section sees changes too. Errors surface as toast
// notifications (set_notification_variable) or inline text for the create form.

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { FaUsers, FaPlus, FaTrashAlt, FaPen, FaTimes, FaCheck } from 'react-icons/fa';
import { set_notification_variable as setNotificationVariable } from '../../../redux/action_creators/home_actions';
import {
  apiErrorReason,
  createTeam,
  renameTeam,
  deleteTeam,
  addTeamMember,
  removeTeamMember
} from './settings_api';
import useArmedConfirm from './settings_hooks';
import SearchableDropdown from './SearchableDropdown';

export default function TeamsSettings({ teams, agents, status, onReload }) {
  const dispatch = useDispatch();
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState('');
  const [busy, setBusy] = useState(false);
  // editingUuid: uuid of the team currently being renamed inline (null = none).
  const [editingUuid, setEditingUuid] = useState(null);
  const [editName, setEditName] = useState('');
  const { armedId, arm, disarm } = useArmedConfirm();

  const toast = (err, fallback) =>
    dispatch(setNotificationVariable(true, 'error', apiErrorReason(err, fallback)));

  const onCreate = () => {
    const name = newName.trim();
    setCreateError('');
    if (!name) {
      setCreateError('Team name is required');
      return;
    }
    setBusy(true);
    createTeam(name)
      .then(() => {
        setNewName('');
        return onReload();
      })
      .catch((err) => setCreateError(apiErrorReason(err, 'Could not create team')))
      .finally(() => setBusy(false));
  };

  const startEdit = (team) => {
    setEditingUuid(team.uuid);
    setEditName(team.name || '');
  };

  const cancelEdit = () => {
    setEditingUuid(null);
    setEditName('');
  };

  const saveEdit = (team) => {
    const name = editName.trim();
    if (!name || name === team.name) {
      cancelEdit();
      return;
    }
    setBusy(true);
    renameTeam(team.uuid, name)
      .then(() => {
        cancelEdit();
        return onReload();
      })
      .catch((err) => toast(err, 'Could not rename team'))
      .finally(() => setBusy(false));
  };

  const onDelete = (team) => {
    // First click arms; second click on the armed team performs the delete.
    if (armedId !== team.uuid) {
      arm(team.uuid);
      return;
    }
    disarm();
    setBusy(true);
    deleteTeam(team.uuid)
      .then(() => onReload())
      .catch((err) => toast(err, 'Could not delete team'))
      .finally(() => setBusy(false));
  };

  const onAddMember = (team, cmsUserUuid) => {
    if (!cmsUserUuid) return;
    setBusy(true);
    addTeamMember(team.uuid, cmsUserUuid)
      .then(() => onReload())
      .catch((err) => toast(err, 'Could not add member'))
      .finally(() => setBusy(false));
  };

  const onRemoveMember = (team, member) => {
    setBusy(true);
    removeTeamMember(team.uuid, member.cms_user_uuid)
      .then(() => onReload())
      .catch((err) => toast(err, 'Could not remove member'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="ta-settings-section">
      <h3 className="ta-settings-heading">Teams</h3>
      <p className="ta-settings-desc">
        Teams group the CMS users who can be rung for a call. Members are CMS users with access; a
        user can belong to multiple teams.
      </p>

      <div className="ta-team-create">
        <input
          className="ta-input"
          placeholder="New team name"
          value={newName}
          onChange={(e) => {
            setNewName(e.target.value);
            if (createError) setCreateError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onCreate();
          }}
        />
        <button
          type="button"
          className="ta-modal-save"
          onClick={onCreate}
          disabled={busy || !newName.trim()}
        >
          <FaPlus size={13} /> Create team
        </button>
      </div>
      {createError && <p className="ta-team-error">{createError}</p>}

      {status === 'loading' && teams.length === 0 && (
        <div className="ta-state-msg">Loading teams…</div>
      )}
      {status === 'fail' && <div className="ta-state-msg">Couldn&apos;t load teams.</div>}
      {status === 'success' && teams.length === 0 && (
        <div className="ta-state-msg">No teams yet. Create one above.</div>
      )}

      <div className="ta-team-list">
        {teams.map((team) => {
          const memberUuids = new Set((team.members || []).map((m) => m.cms_user_uuid));
          const addable = agents.filter((a) => !memberUuids.has(a.uuid));
          return (
            <div key={team.uuid} className="ta-team-card" onMouseLeave={disarm}>
              <div className="ta-team-card-head">
                {editingUuid === team.uuid ? (
                  <span className="ta-team-edit">
                    <input
                      className="ta-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(team);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                    />
                    <button
                      type="button"
                      className="ta-icon-btn"
                      aria-label="Save name"
                      title="Save name"
                      onClick={() => saveEdit(team)}
                      disabled={busy}
                    >
                      <FaCheck size={13} />
                    </button>
                    <button
                      type="button"
                      className="ta-icon-btn"
                      aria-label="Cancel rename"
                      title="Cancel"
                      onClick={cancelEdit}
                      disabled={busy}
                    >
                      <FaTimes size={13} />
                    </button>
                  </span>
                ) : (
                  <span className="ta-team-card-name">
                    <FaUsers size={15} /> {team.name}
                  </span>
                )}

                {editingUuid !== team.uuid && (
                  <span className="ta-team-actions">
                    <button
                      type="button"
                      className="ta-ghost-btn"
                      onClick={() => startEdit(team)}
                      disabled={busy}
                    >
                      <FaPen size={12} /> Rename
                    </button>
                    <span className="ta-del-wrap">
                      <button
                        type="button"
                        className={`ta-team-del ${armedId === team.uuid ? 'ta-del-armed' : ''}`}
                        aria-label={
                          armedId === team.uuid
                            ? `Confirm delete team ${team.name}`
                            : `Delete team ${team.name}`
                        }
                        title={armedId === team.uuid ? 'Click again to confirm' : 'Delete team'}
                        onClick={() => onDelete(team)}
                        disabled={busy}
                      >
                        {armedId === team.uuid ? (
                          <>
                            <FaTrashAlt size={12} /> Confirm delete?
                          </>
                        ) : (
                          <FaTrashAlt size={13} />
                        )}
                      </button>
                      {armedId === team.uuid && (
                        <button
                          type="button"
                          className="ta-arm-cancel"
                          aria-label="Cancel"
                          title="Cancel"
                          onClick={disarm}
                          disabled={busy}
                        >
                          <FaTimes size={13} />
                        </button>
                      )}
                    </span>
                  </span>
                )}
              </div>

              <div className="ta-team-members">
                {(team.members || []).length === 0 && (
                  <span className="ta-team-empty">No members yet.</span>
                )}
                {(team.members || []).map((m) => (
                  <span key={m.cms_user_uuid} className="ta-member-chip">
                    {m.name || m.cms_user_uuid}
                    <button
                      type="button"
                      className="ta-member-remove"
                      aria-label={`Remove ${m.name || 'member'}`}
                      title="Remove member"
                      onClick={() => onRemoveMember(team, m)}
                      disabled={busy}
                    >
                      <FaTimes size={12} />
                    </button>
                  </span>
                ))}
              </div>

              <div className="ta-team-add">
                <SearchableDropdown
                  options={addable}
                  disabled={busy}
                  placeholder="Add member…"
                  emptyPlaceholder="All agents added"
                  searchPlaceholder="Search by name or email…"
                  onSelect={(agent) => onAddMember(team, agent.uuid)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
